import type { ReactNode } from 'react'
import React from 'react'

import { useAuth } from '@fourthwall/auth'
import type { ProviderFeatureFlags } from '@fourthwall/components'
import { FeatureFlagsProvider } from '@fourthwall/components'
import { isProduction } from '@fourthwall/utils/dist/dev'

import { useAdminModeContext } from '../../../modules/App/providers/AdminModeProvider'
import { useCurrentShopQuery } from '../../../modules/Dashboard/hooks/useCurrentShopQuery'
// import { useUsersQuery } from '../../../queries/useUsersQuery'
import { useDashboardPermissions } from '../DashboardPermissions'
import { hasMembershipsMessagesEnabled } from './hasMembershipsMessagesEnabled'
import { hasMembershipsWlaEnabled } from './hasMembershipsWlaEnabled'
import type { Feature } from './types'
import { usePlatformFeatureFlags } from './usePlatformFeatureFlags'

type DashboardFeatureFlagsProviderProps = {
  children: ReactNode
}

export const DashboardFeatureFlagsProvider = ({ children }: DashboardFeatureFlagsProviderProps) => {
  const { user } = useAuth()
  const { hasRole } = useDashboardPermissions()
  const { currentShopQuery } = useCurrentShopQuery(undefined, { staleTime: Infinity })
  const { hasFeatureFlag } = usePlatformFeatureFlags()

  // const { usersQuery } = useUsersQuery(undefined, { staleTime: Infinity })

  // const shopMember =
  //   usersQuery.isSuccess && !!usersQuery.data.users.find(({ email }) => email === user.email)

  const adminMode = useAdminModeContext()

  const admin = {
    enabled: !!adminMode?.isActive,
    description: 'Admins 🔐',
    highlight: true,
  }

  const membershipsEnabled = currentShopQuery.isSuccess

  const featureFlags: ProviderFeatureFlags<Feature> = {
    account: true,
    accountSettingsSecurity:
      hasFeatureFlag('dashboard:account_settings:security') || !!adminMode?.isActive,
    analyticsTabs: hasFeatureFlag('analytics_tabs') || !!adminMode?.isActive,
    analyticsTopProductsProfit:
      hasFeatureFlag('analytics:top_products_profit') || !!adminMode?.isActive,
    analyticsTrackingPixels: true,
    analyticsV2:
      hasFeatureFlag('dashboard:analytics_v2') &&
      (isProduction()
        ? !!adminMode?.isActive &&
          !!user &&
          [
            '2c5c723c-ee1a-4d55-adb8-49ea1f7223b8', // Przemek K
            '5da120b8-08f9-4e06-98c3-3824c0614d15', // Marco M
            '6aac19a9-b3c9-47f3-9d0b-c4313b99f875', // Weronika R
            '7abd5b97-d199-4f5f-921e-732b8866c7cb', // Skotar
            'ceeaae20-978a-4e79-a952-20dcf1318c9c', // Adam P
            '4d62f2df-2f93-4015-80a4-58b6c440a8e2', // Filip J
            '9e9bd884-e258-435b-8e46-6ca6dec7753e', // Kacper B
          ].includes(user.id)
        : true),
    analyticsV2Compare:
      hasFeatureFlag('dashboard:analytics_v2') && hasFeatureFlag('dashboard:analytics_v2:compare'),
    appearanceMode: hasFeatureFlag('appearance_mode'),
    appSpotify: membershipsEnabled && hasFeatureFlag('app_spotify'),
    appLinktree: !!adminMode?.isActive,
    appMerchtrain: hasFeatureFlag('app_merchtrain') || !!adminMode?.isActive,
    appPackiyo: hasFeatureFlag('app_packiyo') || !!adminMode?.isActive,
    appKitv2: !!adminMode?.isActive,
    appMailchimpv2: !!adminMode?.isActive,
    appMeta: hasFeatureFlag('app_meta') || !!adminMode?.isActive,
    buyXGetYPromotion: hasFeatureFlag('buy_x_get_y_promotion'),
    checkoutCustomGtm: hasFeatureFlag('checkout_custom_gtm'),
    createNewSite: hasFeatureFlag('create_new_site'),
    creatorChallenge: true,
    dashboardSaveVisit: hasFeatureFlag('dashboard:save_visit'),
    developersAppManagement: hasFeatureFlag('developers:app_management') || !!adminMode?.isActive,
    developersCustomChatbot: hasFeatureFlag('developers:custom_chatbot') || !!adminMode?.isActive,
    developersHMAC: hasFeatureFlag('developers:hmac') || !!adminMode?.isActive,
    developersSectionSettings:
      hasFeatureFlag('developers:section_settings') || !!adminMode?.isActive,
    giftCards: !!adminMode?.isActive,
    homeOnboarding:
      currentShopQuery.isSuccess &&
      new Date(currentShopQuery.data.createdAt) > new Date('2022-09-08'),
    homeProducts: hasFeatureFlag('dashboard:home_products'),
    homeScheduler: true, // still disabled when !homeOnboarding
    hsCodes: {
      enabled: !!adminMode?.isActive || hasRole('ROLE_COSTS_WRITE'),
      description: 'Admins 🔐',
      highlight: true,
    },
    integrationMeta: hasFeatureFlag('integration_meta'),
    launchStrategy: hasFeatureFlag('launch_strategy'),
    memberships: membershipsEnabled,
    membershipsCustomEmoji: membershipsEnabled && hasFeatureFlag('memberships_custom_emoji'),
    membershipsGifting: membershipsEnabled && hasFeatureFlag('memberships_gifting'),
    membershipsLiveStream: membershipsEnabled && hasFeatureFlag('memberships_live_stream'),
    membershipsMessages:
      membershipsEnabled &&
      currentShopQuery.isSuccess &&
      hasMembershipsMessagesEnabled(currentShopQuery.data.id),
    membershipsPostHtml: membershipsEnabled && hasFeatureFlag('memberships_post_html'),
    membershipsPublicVideo: membershipsEnabled && hasFeatureFlag('memberships_public_video'),
    membershipsWla: membershipsEnabled && hasMembershipsWlaEnabled(currentShopQuery.data.id),
    messagesSendTo: !isProduction(),
    multicurrencyCustomTheme: hasFeatureFlag('multicurrency_custom_theme'),
    multicurrencyCustomThemeYen: hasFeatureFlag('multicurrency_custom_theme_yen'),
    multicurrencySettings: true,
    partner: hasRole('ROLE_AGENCY') || admin,
    payoutSetup: hasRole('ROLE_PAYOUT_SETUP'),
    productAdvancedOptions: {
      enabled: !!adminMode?.isActive || hasRole('ROLE_BESPOKE_READ'),
      description: 'Admins 🔐',
      highlight: true,
    },
    productCategorization: !isProduction(),
    productCombinedOffer: hasFeatureFlag('dashboard:products:create:combined'),
    productBundleOffer: hasFeatureFlag('dashboard:products:create:bundle'),
    productBundleGenerateImage: hasFeatureFlag('dashboard:products:bundle:generate_image'),
    productGiftCard: hasFeatureFlag('dashboard:products:create:giftCard'),
    profitCostsBreakdown: hasFeatureFlag('dashboard:profit_costs_breakdown'),
    promotionCodesFreeShippingLowestOnly: hasFeatureFlag(
      'promotion_codes_free_shipping_lowest_only',
    ),
    reportsFirstTimePurchases: hasFeatureFlag('reports:first_time_purchases'),
    reportsLTV: hasFeatureFlag('reports:ltv'),
    reportsUTM: hasFeatureFlag('reports:utm'),
    storeDesignThemeVersion: admin,
    streamElementsGifting: hasFeatureFlag('stream_elements_gifting'),
    subscriptions: hasFeatureFlag('subscriptions'),
    subscriptionsProductDesignerStorageLimits: !isProduction(),
    subscriptionsStorageLimits: hasFeatureFlag('subscriptions:storage_limits'),
    themeEditorDynamicPreview: hasFeatureFlag('theme_editor:dynamic_preview'),
    themeEditorExperimentalStyles: hasFeatureFlag('theme_editor:experimental_styles'),
    themeEditorFastPreview: hasFeatureFlag('theme_editor:fast_preview'),
    themeEditorGenerateTheme:
      hasFeatureFlag('theme_editor:generate_theme') && !!adminMode?.isActive,
    themeEditorMobileSession: hasFeatureFlag('theme_editor:mobile_session'),
    themeEditorSectionStyles: hasFeatureFlag('theme_editor:section_styles'),
    themeEditorOnboarding: hasFeatureFlag('theme_editor:onboarding'),
    timezone: !!adminMode?.isActive || hasFeatureFlag('dashboard:timezone'),
    thankYouCard: hasFeatureFlag('dashboard:thank_you_card'),
  }

  return <FeatureFlagsProvider featureFlags={featureFlags}>{children}</FeatureFlagsProvider>
}
